import React, { useEffect, useState } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../../components/ui/sheet';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Loader2, UserPlus, Copy, Play, Square, Pencil, Ban, Banknote, KeyRound, Users, FileSignature, ChevronDown, ChevronUp, Archive, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { employeePayApi } from '../api';
import { contractText, CONTRACT_TITLE } from '../../employee/contractText';
import { formatPhoneNumber } from '../components/PhoneInput';

const money = (c) => ((c || 0) / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
const hrs = (s) => `${((s || 0) / 3600).toFixed(2)} hrs`;
const timeOf = (iso) => new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
const dayLabel = (d) => new Date(`${d}T00:00:00`).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
const toLocalInput = (iso) => { const d = new Date(iso); return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16); };
const inputCls = 'bg-black border-gray-700 text-white';
const labelCls = 'text-xs text-gray-400 block mb-1.5';
const err = (e, fb) => { const d = e.response?.data?.detail; return typeof d === 'string' ? d : fb; };
const copyText = async (text, msg) => { try { await navigator.clipboard.writeText(text); toast.success(msg); } catch { window.prompt('Copy this link:', text); } };

const GoldLabel = ({ children }) => <div className="font-brand text-[10px] font-bold uppercase tracking-[0.15em] text-[#D4AF37] mb-0.5">{children}</div>;
const Info = ({ label, value }) => <div><GoldLabel>{label}</GoldLabel><div className="text-sm text-gray-200">{value || '—'}</div></div>;

const InviteDialog = ({ open, onOpenChange }) => {
  const [name, setName] = useState('');
  const [rate, setRate] = useState('');
  const [position, setPosition] = useState('');
  const [busy, setBusy] = useState(false);
  const [link, setLink] = useState(null);
  useEffect(() => { if (open) { setName(''); setRate(''); setPosition(''); setLink(null); } }, [open]);
  const create = async () => {
    const cents = Math.round(parseFloat(rate) * 100);
    if (!name.trim() || !cents || cents <= 0) return toast.error('Name and hourly rate are required');
    setBusy(true);
    try {
      const r = await employeePayApi.createInvite({ name, hourly_rate_cents: cents, position: position || null });
      setLink(`${window.location.origin}${r.data.path}`);
    } catch (e) { toast.error(err(e, 'Could not create invite')); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby={undefined} className="bg-[#111111] border-gray-800 text-white max-w-md" data-testid="invite-dialog">
        <DialogHeader><DialogTitle className="text-white flex items-center gap-2"><UserPlus size={16} className="text-[#D4AF37]" /> Invite Employee</DialogTitle></DialogHeader>
        {link ? (
          <div className="space-y-3">
            <p className="text-sm text-gray-300">Send this link to <b className="text-white">{name}</b>. They'll create their own profile, sign the contractor agreement, and show up here automatically.</p>
            <div className="bg-black border border-gray-800 rounded p-2.5 text-xs text-[#D4AF37] break-all" data-testid="invite-link">{link}</div>
            <Button onClick={() => copyText(link, 'Invite link copied')} className="w-full bg-[#D4AF37] text-black hover:bg-[#B8941F] font-semibold" data-testid="copy-invite-link-btn"><Copy size={14} /> Copy Link</Button>
          </div>
        ) : (
          <div className="space-y-3">
            <div><label className={labelCls}>Employee Name *</label><Input value={name} onChange={(e) => setName(e.target.value)} className={inputCls} data-testid="invite-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className={labelCls}>Hourly Rate ($) *</label><Input value={rate} onChange={(e) => setRate(e.target.value)} placeholder="25.00" className={inputCls} data-testid="invite-rate" /></div>
              <div><label className={labelCls}>Position / Trade</label><Input value={position} onChange={(e) => setPosition(e.target.value)} className={inputCls} data-testid="invite-position" /></div>
            </div>
            <p className="text-[11px] text-gray-500">The employee never sees other profiles — only their own portal. You control the rate.</p>
            <DialogFooter>
              <Button onClick={create} disabled={busy} className="bg-[#D4AF37] text-black hover:bg-[#B8941F] font-semibold w-full" data-testid="create-invite-btn">{busy && <Loader2 size={14} className="animate-spin" />} Generate Invite Link</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const EntryEditDialog = ({ entry, onOpenChange, onSaved }) => {
  const [ci, setCi] = useState(entry ? toLocalInput(entry.clock_in_at) : '');
  const [co, setCo] = useState(entry?.clock_out_at ? toLocalInput(entry.clock_out_at) : '');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await employeePayApi.editEntry(entry.id, {
        clock_in_at: new Date(ci).toISOString(),
        clock_out_at: co ? new Date(co).toISOString() : null,
        reason: reason || null,
      });
      toast.success('Entry corrected'); onOpenChange(false); onSaved();
    } catch (e) { toast.error(err(e, 'Could not save')); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!entry} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby={undefined} className="bg-[#111111] border-gray-800 text-white max-w-md" data-testid="entry-edit-dialog">
        <DialogHeader><DialogTitle className="text-white">Correct Time Entry</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><label className={labelCls}>Clock In</label><Input type="datetime-local" value={ci} onChange={(e) => setCi(e.target.value)} className={inputCls} data-testid="edit-clock-in" /></div>
          <div><label className={labelCls}>Clock Out</label><Input type="datetime-local" value={co} onChange={(e) => setCo(e.target.value)} className={inputCls} data-testid="edit-clock-out" /></div>
          <div><label className={labelCls}>Reason for correction</label><Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} className={inputCls} data-testid="edit-reason" /></div>
          <p className="text-[11px] text-gray-500">The original times are preserved in the correction history — nothing is overwritten silently.</p>
        </div>
        <DialogFooter>
          <Button onClick={save} disabled={busy || !ci} className="bg-[#D4AF37] text-black hover:bg-[#B8941F] font-semibold" data-testid="save-entry-btn">{busy && <Loader2 size={14} className="animate-spin" />} Save Correction</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ContractViewDialog = ({ emp, open, onOpenChange }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent aria-describedby={undefined} className="bg-[#111111] border-gray-800 text-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="contract-view-dialog">
      <DialogHeader><DialogTitle className="text-white flex items-center gap-2"><FileSignature size={16} className="text-[#D4AF37]" /> {CONTRACT_TITLE}</DialogTitle></DialogHeader>
      <div className="bg-black border border-gray-800 rounded-lg p-4 text-xs text-gray-300 whitespace-pre-wrap" data-testid="contract-view-text">
        {contractText(emp.contract?.typed_name || emp.name, emp.contract?.signed_at ? new Date(emp.contract.signed_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : '')}
      </div>
      <div className="border-2 border-[#D4AF37]/50 rounded-lg p-4 bg-black" data-testid="contract-view-signature">
        <div className="font-brand text-[10px] font-bold uppercase tracking-[0.15em] text-[#D4AF37] mb-1">Contractor Signature</div>
        <div className="font-script text-4xl text-[#D4AF37]">{emp.contract?.typed_name}</div>
        <p className="text-xs text-gray-400 mt-2">
          Signed electronically by {emp.contract?.typed_name} on {emp.contract?.signed_at && new Date(emp.contract.signed_at).toLocaleString()}
          {emp.contract?.ip_address && <> · IP {emp.contract.ip_address}</>} · Agreement version {emp.contract?.version}
        </p>
      </div>
    </DialogContent>
  </Dialog>
);

const SECTION_TABS = ['Info', 'Work Hours', 'Edit Work Hours', 'Pay Stubs'];

const EmployeeSheet = ({ empId, onOpenChange, onChanged }) => {
  const [tab, setTab] = useState('Info');
  const [emp, setEmp] = useState(null);
  const [weeks, setWeeks] = useState(null);
  const [entries, setEntries] = useState(null);
  const [stubs, setStubs] = useState(null);
  const [busy, setBusy] = useState(false);
  const [editEntry, setEditEntry] = useState(null);
  const [rateEdit, setRateEdit] = useState('');
  const [contractOpen, setContractOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const load = () => {
    employeePayApi.employee(empId).then((r) => { setEmp(r.data); setRateEdit((r.data.hourly_rate_cents / 100).toFixed(2)); });
    employeePayApi.weeks(empId).then((r) => setWeeks(r.data));
    employeePayApi.entries(empId).then((r) => setEntries(r.data));
    employeePayApi.stubs(empId).then((r) => setStubs(r.data));
  };
  useEffect(() => { if (empId) load(); }, [empId]); // eslint-disable-line

  const act = async (fn, msg) => {
    setBusy(true);
    try { await fn(); if (msg) toast.success(msg); load(); onChanged(); }
    catch (e) { toast.error(err(e, 'Something went wrong')); } finally { setBusy(false); }
  };

  if (!empId) return null;
  return (
    <Sheet open={!!empId} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="bg-[#0D0D0D] border-gray-800 text-white w-full sm:max-w-xl overflow-y-auto p-0" data-testid="employee-sheet">
        {!emp ? (
          <>
            <SheetHeader className="sr-only"><SheetTitle>Loading employee</SheetTitle></SheetHeader>
            <div className="flex justify-center py-20"><Loader2 className="animate-spin text-[#D4AF37]" size={26} /></div>
          </>
        ) : (
          <>
            <SheetHeader className="px-5 pt-5 pb-0">
              <SheetTitle className="text-white font-brand font-extrabold flex items-center gap-2.5">
                {emp.name}
                {emp.active_entry && <span className="text-[9px] font-bold uppercase tracking-wider bg-green-900/40 text-green-400 border border-green-800 rounded px-1.5 py-0.5" data-testid="sheet-active-badge">On the clock</span>}
              </SheetTitle>
              <p className="text-xs text-gray-500 text-left">{emp.position || 'Employee'} · {money(emp.hourly_rate_cents)}/hr{emp.email ? ` · ${emp.email}` : ''}</p>
            </SheetHeader>
            <div className="flex gap-1 px-5 mt-4 border-b border-gray-800 overflow-x-auto">
              {SECTION_TABS.map((t) => (
                <button key={t} onClick={() => setTab(t)} data-testid={`sheet-tab-${t.toLowerCase().replace(/ /g, '-')}`}
                  className={`px-3 py-2 text-xs font-brand font-bold uppercase tracking-wider whitespace-nowrap border-b-2 -mb-px ${tab === t ? 'text-[#D4AF37] border-[#D4AF37]' : 'text-gray-500 border-transparent hover:text-gray-300'}`}>
                  {t}
                </button>
              ))}
            </div>
            <div className="px-5 py-4">
              {tab === 'Info' && (
                <div className="space-y-4" data-testid="sheet-info">
                  <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                    <Info label="Phone" value={emp.phone && formatPhoneNumber(emp.phone)} /><Info label="Email" value={emp.email} />
                    <Info label="Address" value={emp.address} /><Info label="Date of Birth" value={emp.date_of_birth} />
                    <Info label="Emergency Contact" value={emp.emergency_contact_name} /><Info label="Emergency Phone" value={emp.emergency_contact_phone && formatPhoneNumber(emp.emergency_contact_phone)} />
                    <Info label="Joined" value={new Date(emp.created_at).toLocaleDateString()} /><Info label="Login Email" value={emp.email || (emp.username ? `@${emp.username}` : null)} />
                  </div>
                  {emp.contract?.signed ? (
                    <button onClick={() => setContractOpen(true)} className="w-full text-left bg-[#111111] border border-gray-800 rounded-lg p-3 hover:border-[#D4AF37]/60 transition-colors group" data-testid="contract-open-btn">
                      <div className="flex items-center justify-between">
                        <GoldLabel>Contractor Agreement</GoldLabel>
                        <span className="text-[10px] font-brand font-bold uppercase tracking-wider text-gray-500 group-hover:text-[#D4AF37] flex items-center gap-1"><FileSignature size={12} /> View Signed Contract</span>
                      </div>
                      <p className="text-sm text-gray-200" data-testid="contract-status">Signed by <span className="font-script text-lg text-[#D4AF37]">{emp.contract.typed_name}</span> on {new Date(emp.contract.signed_at).toLocaleString()}{emp.contract.ip_address && <span className="text-gray-500 text-xs"> · IP {emp.contract.ip_address}</span>}</p>
                    </button>
                  ) : (
                    <div className="bg-[#111111] border border-gray-800 rounded-lg p-3">
                      <GoldLabel>Contractor Agreement</GoldLabel>
                      <p className="text-sm text-amber-400" data-testid="contract-status">Not signed</p>
                    </div>
                  )}
                  <div className="bg-[#111111] border border-gray-800 rounded-lg p-3">
                    <GoldLabel>Hourly Rate</GoldLabel>
                    <div className="flex gap-2 mt-1">
                      <Input value={rateEdit} onChange={(e) => setRateEdit(e.target.value)} className={`${inputCls} w-28`} data-testid="rate-input" />
                      <Button size="sm" disabled={busy} onClick={() => {
                        const cents = Math.round(parseFloat(rateEdit) * 100);
                        if (!cents || cents <= 0) return toast.error('Enter a valid rate');
                        act(() => employeePayApi.update(emp.id, { hourly_rate_cents: cents }), 'Rate updated (applies to future shifts)');
                      }} className="bg-[#D4AF37] text-black hover:bg-[#B8941F] font-semibold" data-testid="save-rate-btn">Save</Button>
                    </div>
                  </div>
                  <Button variant="outline" disabled={busy} onClick={async () => {
                    try { const r = await employeePayApi.resetLink(emp.id); copyText(`${window.location.origin}${r.data.path}`, 'Reset link copied — send it to the employee'); }
                    catch { toast.error('Could not create reset link'); }
                  }} className="border-[#D4AF37]/50 text-[#D4AF37] w-full" data-testid="reset-link-btn">
                    <KeyRound size={14} /> Send Login Reset Link
                  </Button>
                  <div className="bg-[#111111] border border-gray-800 rounded-lg p-3">
                    <GoldLabel>Employment Status</GoldLabel>
                    <div className="flex items-center justify-between gap-3 mt-1">
                      <span className={`text-sm font-semibold ${emp.is_active !== false ? 'text-green-400' : 'text-red-400'}`} data-testid="employee-status-label">{emp.is_active !== false ? 'Active' : 'Inactive'}</span>
                      {emp.is_active !== false ? (
                        <Button size="sm" variant="outline" disabled={busy} onClick={() => act(() => employeePayApi.update(emp.id, { is_active: false }), `${emp.name} marked inactive`)} className="border-red-900 text-red-400 hover:bg-red-950/40" data-testid="mark-inactive-btn">
                          <Archive size={13} /> Mark Inactive
                        </Button>
                      ) : (
                        <Button size="sm" disabled={busy} onClick={() => act(() => employeePayApi.update(emp.id, { is_active: true }), `${emp.name} reactivated`)} className="bg-green-700 hover:bg-green-600 text-white font-semibold" data-testid="mark-active-btn">
                          Reactivate
                        </Button>
                      )}
                    </div>
                    <p className="text-[11px] text-gray-500 mt-1.5">Inactive employees can't log in to the workforce portal and move to the Inactive Employees folder. Their hours and pay history are kept.</p>
                  </div>
                  <Button variant="outline" disabled={busy} onClick={() => setDeleteOpen(true)} className="border-red-900 text-red-400 hover:bg-red-950/40 hover:text-red-300 w-full" data-testid="delete-employee-btn">
                    <Trash2 size={14} /> Delete Employee Permanently
                  </Button>
                </div>
              )}

              {tab === 'Work Hours' && (
                <div className="space-y-4" data-testid="sheet-work-hours">
                  <div className="flex gap-2">
                    {emp.active_entry ? (
                      <Button disabled={busy} onClick={() => act(() => employeePayApi.clockOut(emp.id), 'Clocked out')} className="flex-1 bg-red-700 hover:bg-red-600 text-white font-semibold" data-testid="ops-clock-out-btn"><Square size={14} /> Clock Out (in since {timeOf(emp.active_entry.clock_in_at)})</Button>
                    ) : (
                      <Button disabled={busy} onClick={() => act(() => employeePayApi.clockIn(emp.id), 'Clocked in')} className="flex-1 bg-[#D4AF37] text-black hover:bg-[#B8941F] font-semibold" data-testid="ops-clock-in-btn"><Play size={14} /> Clock In Manually</Button>
                    )}
                  </div>
                  {weeks === null ? <Loader2 className="animate-spin text-[#D4AF37] mx-auto" size={22} /> : weeks.length === 0 ? <p className="text-gray-500 text-sm text-center py-8">No completed hours yet.</p> : weeks.map((w) => (
                    <div key={w.week_start} className="bg-[#111111] border border-gray-800 rounded-lg p-3">
                      <div className="flex justify-between items-center">
                        <span className="text-white text-sm font-semibold">Week of {dayLabel(w.week_start)}</span>
                        <span className="text-[#D4AF37] font-semibold text-sm">{hrs(w.total_seconds)} · {money(w.total_earnings_cents)}</span>
                      </div>
                      <div className="mt-2 space-y-1">
                        {w.days.map((d) => (
                          <div key={d.date} className="flex justify-between text-xs text-gray-400">
                            <span>{dayLabel(d.date)} — {d.entries.map((e) => `${timeOf(e.clock_in_at)}–${timeOf(e.clock_out_at)}`).join(', ')}</span>
                            <span>{hrs(d.total_seconds)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {tab === 'Edit Work Hours' && (
                <div className="space-y-2" data-testid="sheet-edit-hours">
                  {entries === null ? <Loader2 className="animate-spin text-[#D4AF37] mx-auto" size={22} /> : entries.length === 0 ? <p className="text-gray-500 text-sm text-center py-8">No entries yet.</p> : entries.map((e) => (
                    <div key={e.id} className={`bg-[#111111] border rounded-lg p-3 ${e.is_voided ? 'border-red-900/60 opacity-60' : 'border-gray-800'}`} data-testid={`entry-${e.id}`}>
                      <div className="flex justify-between items-start gap-2">
                        <div>
                          <div className="text-sm text-white font-medium">{dayLabel(e.work_date)} · {timeOf(e.clock_in_at)} – {e.clock_out_at ? timeOf(e.clock_out_at) : <span className="text-green-400">active</span>}</div>
                          <div className="text-xs text-gray-500 mt-0.5">
                            {e.seconds != null && <>{hrs(e.seconds)} · {money(e.earnings_cents)} · </>}by {e.source === 'ops' ? `Ops (${e.created_by})` : 'employee'}
                            {e.is_voided && <span className="text-red-400"> · VOIDED</span>}
                            {e.pay_stub_id && <span className="text-green-500"> · PAID</span>}
                          </div>
                          {e.corrections?.length > 0 && (
                            <div className="mt-1.5 text-[10px] text-amber-500/80 space-y-0.5" data-testid={`corrections-${e.id}`}>
                              {e.corrections.map((c, i) => (
                                <div key={i}>✎ {new Date(c.at).toLocaleString()} by {c.by}{c.reason ? ` — ${c.reason}` : ''} (was {timeOf(c.before.clock_in_at)}–{c.before.clock_out_at ? timeOf(c.before.clock_out_at) : 'active'})</div>
                              ))}
                            </div>
                          )}
                        </div>
                        {!e.pay_stub_id && !e.is_voided && (
                          <div className="flex gap-1 shrink-0">
                            <Button size="sm" variant="outline" onClick={() => setEditEntry(e)} className="border-gray-700 text-gray-300 h-7 px-2" data-testid={`edit-entry-btn-${e.id}`}><Pencil size={12} /></Button>
                            <Button size="sm" variant="outline" disabled={busy} onClick={() => act(() => employeePayApi.voidEntry(e.id, null), 'Entry voided')} className="border-red-900 text-red-400 h-7 px-2" data-testid={`void-entry-btn-${e.id}`}><Ban size={12} /></Button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {tab === 'Pay Stubs' && (
                <div className="space-y-4" data-testid="sheet-pay">
                  <div>
                    <GoldLabel>Weeks to Review</GoldLabel>
                    {weeks === null ? <Loader2 className="animate-spin text-[#D4AF37] mx-auto" size={22} /> : weeks.filter((w) => w.unpaid_seconds > 0).length === 0 ? <p className="text-gray-500 text-sm py-3">No unpaid weeks — all caught up.</p> : weeks.filter((w) => w.unpaid_seconds > 0).map((w) => (
                      <div key={w.week_start} className="bg-[#111111] border border-gray-800 rounded-lg p-3 mt-2 flex items-center justify-between gap-3">
                        <div>
                          <div className="text-white text-sm font-semibold">Week of {dayLabel(w.week_start)}</div>
                          <div className="text-xs text-gray-400">{hrs(w.unpaid_seconds)} unpaid · <span className="text-[#D4AF37] font-semibold">{money(w.unpaid_earnings_cents)}</span></div>
                        </div>
                        <Button size="sm" disabled={busy} onClick={() => act(() => employeePayApi.issueStub(emp.id, w.week_start), 'Marked paid — pay stub issued to the employee portal')}
                          className="bg-green-700 hover:bg-green-600 text-white font-semibold" data-testid={`mark-paid-btn-${w.week_start}`}>
                          <Banknote size={13} /> Mark Paid
                        </Button>
                      </div>
                    ))}
                  </div>
                  <div>
                    <GoldLabel>Issued Stubs</GoldLabel>
                    {stubs === null ? null : stubs.length === 0 ? <p className="text-gray-500 text-sm py-3">No stubs issued yet.</p> : stubs.map((s) => (
                      <div key={s.id} className="bg-[#111111] border border-gray-800 rounded-lg p-3 mt-2 flex justify-between items-center" data-testid={`stub-row-${s.id}`}>
                        <div>
                          <div className="text-white text-sm font-semibold">Week of {dayLabel(s.week_start)}</div>
                          <div className="text-xs text-gray-500">{hrs(s.total_seconds)} · issued {new Date(s.created_at).toLocaleDateString()} by {s.issued_by}</div>
                        </div>
                        <span className="text-[#D4AF37] font-brand font-extrabold">{money(s.gross_cents)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </>
        )}
        {editEntry && <EntryEditDialog entry={editEntry} onOpenChange={(v) => !v && setEditEntry(null)} onSaved={() => { load(); onChanged(); }} />}
        {emp && <ContractViewDialog emp={emp} open={contractOpen} onOpenChange={setContractOpen} />}
        {emp && (
          <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
            <DialogContent aria-describedby={undefined} className="bg-[#111111] border-gray-800 text-white max-w-md" data-testid="delete-employee-dialog">
              <DialogHeader><DialogTitle className="text-red-400 flex items-center gap-2"><Trash2 size={16} /> Delete {emp.name}?</DialogTitle></DialogHeader>
              <p className="text-sm text-gray-300">This <b className="text-red-400">permanently deletes</b> the employee's account, ALL their time entries, and ALL their pay stubs. This cannot be undone.</p>
              <p className="text-xs text-gray-500">If you just want to hide a past employee, use <b className="text-gray-300">Mark Inactive</b> instead — that keeps their records.</p>
              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={() => setDeleteOpen(false)} disabled={busy} className="border-gray-700 text-gray-300">Cancel</Button>
                <Button disabled={busy} onClick={async () => {
                  setBusy(true);
                  try {
                    await employeePayApi.remove(emp.id);
                    toast.success(`${emp.name} deleted permanently`);
                    setDeleteOpen(false); onOpenChange(false); onChanged();
                  } catch (e) { toast.error(err(e, 'Could not delete employee')); } finally { setBusy(false); }
                }} className="bg-red-700 hover:bg-red-600 text-white font-semibold" data-testid="confirm-delete-employee-btn">
                  {busy && <Loader2 size={14} className="animate-spin" />} Delete Permanently
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </SheetContent>
    </Sheet>
  );
};

const EmployeeRow = ({ e, onClick, inactive }) => (
  <button onClick={onClick} className="w-full text-left" data-testid={`employee-row-${e.id}`}>
    <Card className={`bg-[#111111] p-4 hover:border-[#D4AF37]/50 transition-colors flex items-center gap-4 ${inactive ? 'border-gray-800 opacity-70' : e.active_entry ? 'border-green-800/70 border-l-4 border-l-green-500' : 'border-gray-800'}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-white font-semibold">{e.name}</span>
          {inactive && <span className="text-[9px] font-brand font-bold uppercase tracking-wider bg-red-950/50 text-red-400 border border-red-900 rounded px-1.5 py-0.5">Inactive</span>}
          {!inactive && e.active_entry && <span className="text-[9px] font-brand font-bold uppercase tracking-wider bg-green-900/40 text-green-400 border border-green-800 rounded px-1.5 py-0.5">On the clock · since {timeOf(e.active_entry.clock_in_at)}</span>}
        </div>
        <p className="text-xs text-gray-500 mt-0.5">{e.position || 'Employee'} · {money(e.hourly_rate_cents)}/hr</p>
      </div>
      <div className="text-right shrink-0">
        <div className="text-sm text-[#D4AF37] font-semibold">{money(e.week_earnings_cents)} <span className="text-gray-500 font-normal text-xs">this wk</span></div>
        <div className="text-xs text-gray-500">{hrs(e.week_seconds)}{e.unpaid_earnings_cents > 0 && <span className="text-amber-400"> · {money(e.unpaid_earnings_cents)} unpaid</span>}</div>
      </div>
    </Card>
  </button>
);

export default function OpsEmployeePay() {
  const [employees, setEmployees] = useState(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [inactiveOpen, setInactiveOpen] = useState(false);

  const load = () => employeePayApi.employees().then((r) => setEmployees(r.data)).catch(() => toast.error('Could not load employees'));
  useEffect(() => { load(); }, []);

  const active = (employees || []).filter((e) => e.is_active !== false);
  const inactive = (employees || []).filter((e) => e.is_active === false);

  return (
    <div data-testid="ops-employee-pay-page">
      <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
        <div>
          <h2 className="text-xl font-bold text-white">Employee Pay Tracking</h2>
          <p className="text-sm text-gray-400 mt-0.5">Employees with portal accounts. Click a name for details.</p>
        </div>
        <Button onClick={() => setInviteOpen(true)} className="bg-[#D4AF37] text-black hover:bg-[#B8941F] font-semibold" data-testid="invite-employee-btn">
          <UserPlus size={15} /> Invite Employee
        </Button>
      </div>

      {employees === null ? <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#D4AF37]" size={26} /></div>
        : employees.length === 0 ? (
          <Card className="bg-[#111111] border-gray-800 p-10 text-center" data-testid="employees-empty-state">
            <Users size={36} className="text-gray-600 mx-auto mb-3" />
            <p className="text-gray-400">No employee accounts yet. Send someone an invite link to get started.</p>
          </Card>
        ) : (
          <>
            {active.length === 0 ? (
              <Card className="bg-[#111111] border-gray-800 p-8 text-center" data-testid="no-active-employees">
                <p className="text-gray-400 text-sm">No active employees.</p>
              </Card>
            ) : (
              <div className="space-y-2">
                {active.map((e) => <EmployeeRow key={e.id} e={e} onClick={() => setSelected(e.id)} />)}
              </div>
            )}

            {inactive.length > 0 && (
              <div className="mt-8">
                <button onClick={() => setInactiveOpen((v) => !v)} className="w-full text-left" data-testid="inactive-folder-toggle">
                  <Card className="bg-[#0D0D0D] border-gray-800 p-4 flex items-center gap-3 hover:border-gray-600 transition-colors">
                    <Archive size={18} className="text-gray-500" />
                    <div className="flex-1">
                      <span className="text-gray-300 font-brand font-bold uppercase tracking-wider text-xs">Inactive Employees</span>
                      <span className="text-gray-600 text-xs ml-2">({inactive.length})</span>
                    </div>
                    {inactiveOpen ? <ChevronUp size={16} className="text-gray-500" /> : <ChevronDown size={16} className="text-gray-500" />}
                  </Card>
                </button>
                {inactiveOpen && (
                  <div className="space-y-2 mt-2" data-testid="inactive-employees-list">
                    {inactive.map((e) => <EmployeeRow key={e.id} e={e} onClick={() => setSelected(e.id)} inactive />)}
                  </div>
                )}
              </div>
            )}
          </>
        )}

      <InviteDialog open={inviteOpen} onOpenChange={setInviteOpen} />
      <EmployeeSheet empId={selected} onOpenChange={(v) => !v && setSelected(null)} onChanged={load} />
    </div>
  );
}
